(ns dre.pages.dictionary.subclass-graph
  (:require
   [applied-science.js-interop :as j]
   ["d3" :as d3]
   [dre.app.router :as router :refer [->route]]
   [dre.pages.dictionary.events :as events]
   [dre.utils.bem :as bem]
   [goog.dom :as gdom]
   [reagent.core :as r]
   [re-frame.core :as rf]
   ["react-sizeme" :as react-sizeme]))

(def component-name :subclass-graph)

;; Public API

(defn graph
  [element data w _]
  (let [relations (:dict/gather-by-cui data)
        data relations
        width w
        d3-el (j/call d3 :select element)
        svg (.. (j/call d3-el :append "svg")
                (attr "fill" "#d046ff"))

        data-js (clj->js data)
        root (j/call d3 :hierarchy data-js)
        dx 10
        dy (/ width (+ (j/get root :height) 1))
        tree (.. d3
                 tree
                 (nodeSize #js [dx dy]))
        _ (tree root)
        xd (j/obj :x0 js/Infinity :x1 (* -1 js/Infinity))
        _ (j/call root :each (fn [d]
                               (when (> (j/get d :x) (j/get xd :x1)) (j/assoc! xd :x1 (j/get d :x)))
                               (when (< (j/get d :x) (j/get xd :x0)) (j/assoc! xd :x0 (j/get d :x)))))
        height  (+ (- (j/get xd :x1) (j/get xd :x0)) (* dx 2))
        _ (rf/dispatch [::events/set-subclass-height height])

        node (.. svg
                 (append "g")
                 (attr "stroke-linejoin" "round")
                 (attr "stroke-width" 3)
                 (selectAll)
                 (data (j/call root :descendants))
                 (join "g")
                 (attr "transform" (fn [d] (str "translate(" (j/get d :y) "," (j/get d :x) ")"))))
        nodes-by-uri (into {}
                           (for [n (j/call root :descendants)
                                 uri (cons
                                      (j/get-in n [:data :uri])
                                      (map #(j/get-in % [:data :uri]) (j/get-in n [:data :sub-nodes])))]
                             [uri n]))
        links (j/call root :links)
        _ (doseq [node (j/call root :descendants)]
            (when-let [ref-children
                       (map
                        (fn [ref]
                          (j/get ref :uri))
                        (j/get-in node [:data :ref-children]))]
              (doseq [c ref-children]
                (when-let [target (get nodes-by-uri c)]
                  (when (not (j/call links :some (fn [e] (or 
                                                          (and (= (j/get e :target) node) (= (j/get e :source) target))
                                                          (and (= (j/get e :source) node) (= (j/get e :target) target))))))
                   (.push links #js {:source node
                                    :target target}))))))
        link (.. svg
                 (append "g")
                 (attr "fill" "none")
                 (attr "stroke" "#555")
                 (attr "stroke-opacity" 0.4)
                 (attr "stroke-width" 1.5)
                 (selectAll)
                 (data links)
                 (join "path")
                 (attr "d" (.. d3
                               linkHorizontal
                               (x (fn [d] (j/get d :y)))
                               (y (fn [d] (j/get d :x))))))]
    (.. svg
        (attr "width" width)
        (attr "height" height)
        (attr "style" "max-width: 100%; height: auto;")
        (attr "viewBox" (into-array [(/ (* -1 dy) 3) (- (j/get xd :x0) dx) width height])))
    (.. node
        (append "circle")
        (attr "fill" "#555")
        (attr "r" 3))
    (.. node
        (append "text")
        (attr "dy" "0.31em")
        (attr "x" (fn [d] (if (j/get d :children) -6 6)))
        (attr "text-anchor" (fn [d] (if (j/get d :children) "end" "start")))
        (text (fn [d]
                (when (j/get-in d [:parent])
                  (j/get-in d [:data :name]))))
        (attr "fill" "#555"))
    (.. node
        (append "title")
        (attr "dy" "0.31em")
        (attr "x" (fn [d] (if (j/get d :children) -6 6)))
        (attr "text-anchor" (fn [d] (if (j/get d :children) "end" "start")))
        (text (fn [d]
                (when (j/get-in d [:parent])
                  (j/get-in d [:data :name]))))
        (attr "fill" "#555"))
    (.. node 
        (on "mouseover" (fn [e d]
                          (j/call node :classed "active-node" (fn [l] (= l d)))))
        (on "mouseout" (fn [e d]
                          (j/call node :classed "active-node" false)))
        (on "click" (fn [e d] 
                      (let [route-params (events/route-params (j/get-in d [:data :guid-uri]))
                            r (->route :dictionary {:guid (:guid route-params)})]
                        (rf/dispatch [::router/redirect-to-path r])))))))

(defn subclass-graph*
  [data width height]
  (let [element (atom nil)
        destroy!
        (fn [_this]
          (when @element
            (gdom/removeChildren @element)))
        repaint!
        (fn [this data width height]
          (destroy! this)
          (graph @element data width height))]
    (r/create-class
     {:component-did-mount
      (fn [this]
        (repaint! this data width height))
      :component-will-unmount
      (fn [this]
        (destroy! this))
      :component-will-update
      (fn [this [_ data width height]]
        (repaint! this data width height))
      :reagent-render
      (fn [data width height]
        [:div
         {:style
          {:width "100%"
           :height "100%"}}
         [:div
          {:ref #(reset! element %)
           :style
           {:width width
            :height height}}]])})))

(defn subclass-graph
  [data]
  [:div {:class component-name}
   [(r/adapt-react-class react-sizeme/SizeMe)
    {:monitorWidth true :monitorHeight true}
    (fn [props]
      (let [props (js->clj props :keywordize-keys true)]
        (r/as-element [subclass-graph*
                       data
                       (:width (:size props))
                       (:height (:size props))])))]])
